import { useIntersectionObserver, UseIntersectionObserverOptions } from '@vueuse/core';
import {
	ref, type Ref, computed,
	MaybeRef,
	onBeforeUnmount,
	type ComputedRef,
} from 'vue';

// https://stackoverflow.com/a/2117523
export function generateUUID(): string {
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
		/[xy]/g,
		function(c) {
			const r = Math.trunc(Math.random() * 16);
			// eslint-disable-next-line no-bitwise, eqeqeq
			const v = c == 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		},
	);
}

// Collecting stop functions for each observer
const observerRepository: Map<string, () => void> = new Map();
// Stores the intersection state of each observer based on the isIntersecting value of the observer callback
const intersections: Map<string, Ref<boolean>> = new Map();
// Stores the expected visibility state of each element to make the isInBounds computed property true
const visibilities = new Map<string, boolean>();
// Computed property to check if the boundary conditions are met
const isInBounds = computed(() => Array.from(intersections.entries()).every(([uuid, intersection]) => intersection.value === visibilities.get(uuid)));

// Composable to share a compound boundary condition between multiple applications
// It uses the useIntersectionObserver composable to check the visibility of different elements across a page,
// and returns the required visibility state state based on the compound fulfillment of the shouldBeVisible predicate of each element

// @params arbitrary number of objects with elementRef, shouldBeVisible and options properties

// @note the shouldBeVisible property is a boolean that indicates if the element should be visible or not for the boundary condition to be met

export function useBoundaryObserver(...elementsToWatch: Array<{
	elementRef: MaybeRef<HTMLElement | null | undefined> | ComputedRef<HTMLElement | null | undefined>,
	shouldBeVisible: boolean
	options?: UseIntersectionObserverOptions
}>) {
	elementsToWatch.forEach(({ elementRef, shouldBeVisible, options }) => {
		const uuid = generateUUID();
		intersections.set(uuid, ref(false));
		visibilities.set(uuid, shouldBeVisible);
		const { stop } = useIntersectionObserver(elementRef, ([{ isIntersecting }]) => {
			intersections.get(uuid)!.value = isIntersecting;
		}, options);
		observerRepository.set(uuid, stop);

		onBeforeUnmount(() => {
			observerRepository.get(uuid)!();
			intersections.delete(uuid);
		});
	});

	return { isInBounds };
}
